import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {DatePipe, NgClass} from '@angular/common';
import {PatientFileModel} from '../../../models/patient-file/patient-file.model';
import {RecipeModel} from '../../../models/recipe/recipe.model';
import {Router} from '@angular/router';
import {PatientModel} from '../../../models/patient/patient.model';
import {RecipeService} from '../../../services/recipe/recipe.service';
import {iResponse, iResponseList} from '../../../models/response/response.model';
import {InfoMessageService} from '../../../services/infomessage/infomessage.service';
import {MatTabsModule} from '@angular/material/tabs';
import {PatientService} from '../../../services/patient/patient.service';

@Component({
    selector: 'app-patient-recipe',
    imports: [
        DatePipe,
        NgClass,
        MatTabsModule
    ],
    templateUrl: './patient-recipe.component.html',
    styleUrl: './patient-recipe.component.sass',
    standalone: true
})
export class PatientRecipeComponent implements OnInit, OnDestroy {
    @Input() patientFile!: PatientFileModel
    @Input() patient!: PatientModel

    upcomingRecipes: RecipeModel[] = [];
    pastRecipes: RecipeModel[] = [];

    private destroy$ = new Subject<void>()

    isLoadingFollowRecipe: boolean = false;

    constructor(
        private router: Router,
        private recipeService: RecipeService,
        private infoMessageService: InfoMessageService,
        private patientService: PatientService
    ) {
    }

    ngOnInit(): void {
        if (!this.patientFile || !this.patient) {
            this.loadInitialData();
        } else {
            this.sortRecipes();
        }
    }

    private loadInitialData(): void {
        this.patientService.getMe().pipe(takeUntil(this.destroy$)).subscribe({
            next: (response: iResponse<PatientModel>) => {
                this.patient = response.data;
                this.patientService.getPatientFiles(this.patient.id).pipe(takeUntil(this.destroy$)).subscribe({
                    next: (res: iResponseList<PatientFileModel>) => {
                        if (res.data && res.data.length > 0) {
                            this.patientFile = res.data[0];
                            this.sortRecipes();
                        }
                    }
                });
            }
        });
    }

    private sortRecipes(): void {
        if (!this.patientFile || !this.patientFile.prescriptions) {
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        this.upcomingRecipes = this.patientFile.prescriptions
            .filter(r => new Date(r.issueDate) >= today)
            .sort((a, b) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime());

        this.pastRecipes = this.patientFile.prescriptions
            .filter(r => new Date(r.issueDate) < today)
            .sort((a, b) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    routeToFollowRecipe(prescription: RecipeModel, edit: boolean = false) {
        if (edit) {
            if (this.patient) {
                this.patient.calendarOptions = undefined;
            }
            this.router.navigate(['/folgerezept'], {state: {recipe: prescription, patient: this.patient}})
        } else {
            this.requestFollowingPrescription(prescription)
        }
    }

    requestFollowingPrescription(prescription: RecipeModel) {
        this.isLoadingFollowRecipe = true;
        this.recipeService.copyPrescription(prescription.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<RecipeModel>) => {
                    const copy = response.data;
                    const update = {
                        id: copy.id,
                        articles: prescription.positions
                    };

                    this.recipeService.updateRecipe(copy.id, update)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: () => {
                                this.recipeService.notifyForDoctorReview(copy.id)
                                    .pipe(takeUntil(this.destroy$))
                                    .subscribe({
                                        next: () => {
                                            this.infoMessageService.createMessage('Das Folgerezept wurde an den Arzt zur Freigabe gesendet', 'success');
                                            this.loadInitialData();
                                            this.isLoadingFollowRecipe = false;
                                        },
                                        error: () => {
                                            this.infoMessageService.createMessage('Fehler beim Benachrichtigen des Arztes', 'error');
                                            this.isLoadingFollowRecipe = false;
                                        }
                                    });
                            },
                            error: () => {
                                this.infoMessageService.createMessage('Fehler beim Aktualisieren des Rezepts', 'error');
                                this.isLoadingFollowRecipe = false;
                            }
                        });
                },
                error: () => {
                    this.infoMessageService.createMessage('Fehler beim Kopieren des Rezepts', 'error');
                    this.isLoadingFollowRecipe = false;
                }
            });
    }

    navigateToPortal(prescription: RecipeModel) {
        if (prescription && prescription.paymentInfos && prescription.paymentInfos.paymentLink) {
            window.open(prescription.paymentInfos.paymentLink, '_blank');
        } else {
            this.infoMessageService.createMessage('Es wurden noch keine Zahlungsdaten erstellt', 'info');
        }
    }
}
